import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { showModal } from "../statusModal/statusModal";
import { setTournamentNextStagePlan } from "../tables/tableSlice";
import {
  addPlayerToTournamentAPI,
  deletePlayerFromTableAPI,
  fetchTablePlayersByIdAPI,
  fetchTournamentEntriesByTableIdsAPI,
  moveEntryToFinalStageAPI,
  moveEntryToNextStageAPI,
  movePlayersAPI,
  setTournamentEntryCheckedAPI,
} from "./playersAPI";

const initialState = {
  allPlayers: {},
  selectedPlayers: [],
  cutPlayers: [],
  cutFromTableId: "",
  lastSelected: null,
  data: {},
  tables: {},
  status: {},
  error: {},
  addPlayerToTouranemntStatus: "idle",
  entryCheckStatusByRowId: {},
};

const toStringId = (value) => String(value || "").trim();

const getEntryRowId = (entry) => {
  return (
    toStringId(entry?.rowId) ||
    (entry?.kind === "squad"
      ? toStringId(entry?.squadId || entry?._id)
      : toStringId(entry?.userId || entry?._id))
  );
};

const normalizeEntry = (entry) => {
  const kind = entry?.kind === "squad" ? "squad" : "user";
  const rowId = getEntryRowId(entry);

  return {
    ...entry,
    kind,
    rowId,
    userId: kind === "user" ? entry?.userId || entry?._id || null : null,
    squadId: kind === "squad" ? entry?.squadId || entry?._id || null : null,
    highlighted: Boolean(entry?.highlighted),
    isChecked: Boolean(entry?.isChecked),
  };
};

const getEntryActionRowId = (payload = {}) => {
  const kind = payload?.kind === "squad" ? "squad" : "user";
  return kind === "squad"
    ? toStringId(payload?.squadId)
    : toStringId(payload?.userId);
};

const buildEntryMoves = (payload = {}) => {
  if (Array.isArray(payload.entryMoves) && payload.entryMoves.length > 0) {
    return payload.entryMoves;
  }

  if (Array.isArray(payload.playerMoves) && payload.playerMoves.length > 0) {
    return payload.playerMoves
      .filter((move) => move?.userId && move?.fromTableId)
      .map((move) => ({
        kind: "user",
        userId: move.userId,
        fromTableId: move.fromTableId,
      }));
  }

  return [];
};

const findEntryIndex = (rows = [], move = {}) => {
  if (move.kind === "squad") {
    const targetId = toStringId(move.squadId);
    return rows.findIndex(
      (row) =>
        row?.kind === "squad" &&
        toStringId(row?.squadId || row?._id) === targetId,
    );
  }

  const targetId = toStringId(move.userId);
  return rows.findIndex(
    (row) =>
      row?.kind !== "squad" &&
      toStringId(row?.userId || row?._id) === targetId,
  );
};

const applyTableEntriesPayloadToState = ({
  state,
  tableId,
  table,
  entries,
}) => {
  if (!tableId) return;

  if (table) {
    state.tables[tableId] = table;
  }

  state.data[tableId] = (Array.isArray(entries) ? entries : [])
    .filter((entry) => entry && (entry._id || entry.userId || entry.squadId))
    .map((entry) => normalizeEntry(entry));

  state.status[tableId] = "succeeded";
  state.error[tableId] = null;
};

export const fetchTablePlayersById = createAsyncThunk(
  "player/fetchTablePlayersById",
  async ({ tableId, tournamentId, silent = false }, { dispatch, rejectWithValue }) => {
    try {
      const data = await fetchTablePlayersByIdAPI({ tableId, tournamentId });
      return data;
    } catch (error) {
      if (!silent) {
        dispatch(showModal({ message: "حصل خطأ اثناء جلب البيانات", status: "error" }));
      }
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch players",
      );
    }
  },
);

export const fetchTournamentEntriesByTableIds = createAsyncThunk(
  "player/fetchTournamentEntriesByTableIds",
  async (
    { tournamentId, tableIds = [], silent = false },
    { dispatch, rejectWithValue },
  ) => {
    const normalizedTableIds = [...new Set(
      (Array.isArray(tableIds) ? tableIds : [])
        .map((tableId) => String(tableId || "").trim())
        .filter(Boolean),
    )];

    if (!normalizedTableIds.length) {
      return { data: { tables: [] } };
    }

    try {
      const data = await fetchTournamentEntriesByTableIdsAPI({
        tournamentId,
        tableIds: normalizedTableIds,
      });
      return data;
    } catch (error) {
      if (!silent) {
        dispatch(showModal({ message: "حصل خطأ اثناء جلب البيانات", status: "error" }));
      }
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch tournament entries",
      );
    }
  },
);

// "Move to next stage / finals" found no table for the entry: every table is
// full (maxFree 0), or none has the seats this squad needs.
const noRoomMessage = (stage) => (params) => {
  const maxFree = Number(params?.maxFree) || 0;
  if (maxFree === 0) return `جميع جداول ${stage} ممتلئة`;
  const seats = Number(params?.seats) || 0;
  const needed = seats ? `المقاعد المطلوبة: ${seats}، ` : "";
  return `لا يوجد جدول في ${stage} يتسع لهذا العنصر (${needed}أكثر مقاعد متاحة في جدول: ${maxFree})`;
};

// Moves, adds and removals the backend refuses (4xx + errorKey) → what the
// admin reads. Anything not listed keeps the action's own generic message.
const ENTRY_ERROR_MESSAGES = {
  "errors.table.destinationFull": () => "الجدول المختار ممتلئ، اختر جدولًا آخر",
  "errors.table.destinationOverLimit": (params) =>
    `الجدول المختار لا يتسع لكل العناصر المحددة (المقاعد المتاحة: ${params?.free ?? 0})`,
  "errors.table.nextStageNoRoom": noRoomMessage("المرحلة المتقدمة"),
  "errors.table.finalNoRoom": noRoomMessage("النهائيات"),
  // Changed meanwhile (another admin, a double click): the page is out of date.
  "errors.table.entryNotInTable": () =>
    "أحد العناصر لم يعد في هذا الجدول (ربما نُقل أو حُذف)، حدّث الصفحة",
  "errors.table.notFound": () => "الجدول لم يعد موجودًا، حدّث الصفحة",
  "errors.table.noNextStageTables": () =>
    "لا توجد جداول للمرحلة المتقدمة بعد، أنشئها أولًا",
  "errors.table.noFinalTables": () => "لا توجد جداول للنهائيات بعد، أنشئها أولًا",
  "errors.table.sameTable": () => "لا يمكن النقل إلى نفس الجدول",
  "errors.table.busy": () => "الجدول مشغول بعملية أخرى الآن، حاول مرة أخرى",
  // Adding an entry.
  "errors.tournament.noTable": () =>
    "لا يوجد مكان في هذا الجدول (ممتلئ، أو لا يتسع لكل أعضاء السكواد)",
  "errors.tournament.noTouranment": () => "البطولة لم تعد موجودة، حدّث الصفحة",
  "errors.user.notFound": () => "اللاعب غير موجود",
  "errors.tournament.userIsBanned": () => "هذا اللاعب محظور ولا يمكن إضافته",
  "errors.squad.userNotInSquad": () => "السكواد غير موجود",
  "errors.squad.squadIsBanned": () => "هذا السكواد محظور ولا يمكن إضافته",
  "errors.squad.membersNotFound": () => "لا يوجد أعضاء نشطون في هذا السكواد",
  // Any of them.
  "errors.auth.forbidden": () => "ليس لديك صلاحية على هذا الجدول",
  "errors.rateLimit.tooManyRequests": () => "طلبات كثيرة، انتظر قليلًا ثم حاول مرة أخرى",
};

const entryRefusalMessage = (error) => {
  const data = error.response?.data;
  const known = Object.prototype.hasOwnProperty.call(ENTRY_ERROR_MESSAGES, data?.errorKey);
  return known ? ENTRY_ERROR_MESSAGES[data.errorKey](data.params) : null;
};

export const movePlayers = createAsyncThunk(
  "player/movePlayers",
  async ({ toTableId, tournamentId, ...payload }, { dispatch, rejectWithValue }) => {
    try {
      const entryMoves = buildEntryMoves(payload);
      if (!entryMoves.length) {
        throw new Error("No entries selected for moving");
      }

      await movePlayersAPI({ entryMoves, toTableId });

      const fromTableIds = [...new Set(entryMoves.map((move) => move.fromTableId))];
      const reloadTableIds = [...new Set([toTableId, ...fromTableIds])];

      await Promise.all(
        reloadTableIds.map((tableId) =>
          dispatch(fetchTablePlayersById({ tableId, tournamentId, silent: true })),
        ),
      );

      dispatch(showModal({ message: "تم نقل العناصر بنجاح", status: "success" }));

      return { entryMoves, toTableId };
    } catch (error) {
      dispatch(
        showModal({
          message: entryRefusalMessage(error) || "حصل خطأ اثناء نقل العناصر",
          status: "error",
        }),
      );
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to move entries",
      );
    }
  },
);

const createMoveToStageThunk = ({ typePrefix, apiCall, successMessage, errorMessage }) =>
  createAsyncThunk(
    typePrefix,
    async (
      {
        tournamentId,
        fromTableId,
        kind = "user",
        userId = null,
        squadId = null,
      },
      { dispatch, rejectWithValue },
    ) => {
      try {
        const response = await apiCall({
          tournamentId,
          fromTableId,
          kind,
          userId,
          squadId,
        });

        const payloadData = response?.data || {};
        const toTableId = payloadData?.toTableId;
        const reloadTableIds = [...new Set([fromTableId, toTableId].filter(Boolean))];

        if (reloadTableIds.length) {
          await Promise.all(
            reloadTableIds.map((tableId) =>
              dispatch(fetchTablePlayersById({ tableId, tournamentId, silent: true })),
            ),
          );
        }

        if (payloadData?.nextStagePlan) {
          dispatch(
            setTournamentNextStagePlan({
              tournamentId,
              nextStagePlan: payloadData.nextStagePlan,
            }),
          );
        }

        dispatch(showModal({ message: successMessage, status: "success" }));

        return payloadData;
      } catch (error) {
        dispatch(
          showModal({
            message:
              entryRefusalMessage(error) || errorMessage,
            status: "error",
          }),
        );
        return rejectWithValue(
          error.response?.data?.error ||
            error.response?.data?.message ||
            error.message ||
            errorMessage,
        );
      }
    },
  );

export const moveEntryToNextStage = createMoveToStageThunk({
  typePrefix: "player/moveEntryToNextStage",
  apiCall: moveEntryToNextStageAPI,
  successMessage: "تم نقل العنصر للمرحلة المتقدمة بنجاح",
  errorMessage: "فشل نقل العنصر للمرحلة المتقدمة",
});

export const moveEntryToFinalStage = createMoveToStageThunk({
  typePrefix: "player/moveEntryToFinalStage",
  apiCall: moveEntryToFinalStageAPI,
  successMessage: "تم نقل العنصر للنهائيات بنجاح",
  errorMessage: "فشل نقل العنصر للنهائيات",
});

export const deletePlayerFromTable = createAsyncThunk(
  "player/deletePlayerFromTable",
  async (
    { tournamentId, tableId, kind = "user", userId = null, squadId = null },
    { dispatch, rejectWithValue },
  ) => {
    try {
      await deletePlayerFromTableAPI({
        tournamentId,
        tableId,
        kind,
        userId,
        squadId,
      });

      await dispatch(
        fetchTablePlayersById({ tableId, tournamentId, silent: true }),
      );

      dispatch(showModal({ message: "تم حذف العنصر بنجاح", status: "success" }));

      return { tournamentId, tableId, kind, userId, squadId };
    } catch (error) {
      dispatch(
        showModal({
          message: entryRefusalMessage(error) || "فشل حذف العنصر",
          status: "error",
        }),
      );
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete entry",
      );
    }
  },
);

export const addPlayerToTournament = createAsyncThunk(
  "player/addPlayerToTournament",
  async (
    {
      tournamentId,
      tableId,
      kind = "user",
      userId = null,
      squadId = null,
    },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const response = await addPlayerToTournamentAPI({
        tournamentId,
        tableId,
        kind,
        userId,
        squadId,
      });

      await dispatch(
        fetchTablePlayersById({ tableId, tournamentId, silent: true }),
      );

      dispatch(showModal({ message: "تم اضافة العنصر بنجاح", status: "success" }));

      return response.data;
    } catch (error) {
      dispatch(
        showModal({
          message: entryRefusalMessage(error) || "فشل اضافة العنصر",
          status: "error",
        }),
      );
      return rejectWithValue(
        error.response?.data?.message || "Failed to add entry",
      );
    }
  },
);

export const setTournamentEntryChecked = createAsyncThunk(
  "player/setTournamentEntryChecked",
  async (
    {
      tournamentId,
      tableId,
      kind = "user",
      userId = null,
      squadId = null,
      isChecked = false,
    },
    { dispatch, rejectWithValue },
    ) => {
      try {
        const response = await setTournamentEntryCheckedAPI({
          tournamentId,
        tableId,
        kind,
        userId,
        squadId,
          isChecked,
        });

        // The checkbox's own words. One message per state, so ticking a
        // column of players keeps a single box on screen (identical toasts
        // replace each other).
        const entryName = kind === "squad" ? "الفريق" : "اللاعب";
        dispatch(
          showModal({
            message: isChecked
              ? `تم تحديد ${entryName}`
              : `تم إلغاء تحديد ${entryName}`,
            status: "success",
          }),
        );

        return {
          ...(response?.data || {}),
          tableId,
          kind,
          userId,
          squadId,
          isChecked: Boolean(isChecked),
        };
      } catch (error) {
        dispatch(
          showModal({
          message:
            kind === "squad" ? "تعذّر تحديد الفريق" : "تعذّر تحديد اللاعب",
          status: "error",
        }),
      );
      return rejectWithValue(
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message ||
          "Failed to update entry check status",
      );
    }
  },
);

const playersSlice = createSlice({
  name: "players",
  initialState,
  reducers: {
    setSelectedPlayers: (state, action) => {
      state.selectedPlayers = action.payload.selected || [];
      if (action.payload.lastSelected !== undefined) {
        state.lastSelected = action.payload.lastSelected;
      }
    },
    resetSelectedPlayers: (state) => {
      state.selectedPlayers = [];
    },
    setCutPlayers: (state, action) => {
      state.cutPlayers = action.payload;
    },
    clearCutPlayers: (state) => {
      state.cutPlayers = [];
    },
    highlightPlayer: (state, action) => {
      const { tableId, playerIds } = action.payload;
      const normalizedIds = playerIds.map((id) => toStringId(id));

      state.data[tableId] = state.data[tableId]?.map((player) => {
        const rowId = getEntryRowId(player);
        return normalizedIds.includes(rowId)
          ? { ...player, highlighted: true }
          : { ...player, highlighted: false };
      });
    },
    resetHighlights: (state) => {
      Object.keys(state.data).forEach((tableId) => {
        state.data[tableId] = state.data[tableId]?.map((player) => ({
          ...player,
          highlighted: false,
        }));
      });
    },
    setCutFromTableId: (state, action) => {
      state.cutFromTableId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTablePlayersById.pending, (state, action) => {
        const { tableId } = action.meta.arg;
        state.status[tableId] = "loading";
        state.error[tableId] = null;
      })
      .addCase(fetchTablePlayersById.fulfilled, (state, action) => {
        const payloadData = action.payload?.data || {};
        const tableId = payloadData.tableId || action.meta.arg.tableId;
        const entries = Array.isArray(payloadData.entries)
          ? payloadData.entries
          : Array.isArray(payloadData.players)
            ? payloadData.players
            : [];
        applyTableEntriesPayloadToState({
          state,
          tableId,
          table: payloadData.table,
          entries,
        });
      })
      .addCase(fetchTablePlayersById.rejected, (state, action) => {
        const tableId = action.meta.arg?.tableId;
        if (tableId) {
          state.status[tableId] = "failed";
          state.error[tableId] = action.payload || action.error.message;
        }
      })

      .addCase(fetchTournamentEntriesByTableIds.pending, (state, action) => {
        const tableIds = Array.isArray(action.meta.arg?.tableIds)
          ? action.meta.arg.tableIds
          : [];

        tableIds.forEach((tableId) => {
          const normalizedTableId = String(tableId || "").trim();
          if (!normalizedTableId) return;
          state.status[normalizedTableId] = "loading";
          state.error[normalizedTableId] = null;
        });
      })
      .addCase(fetchTournamentEntriesByTableIds.fulfilled, (state, action) => {
        const payloadData = action.payload?.data || {};
        const tablePayloads = Array.isArray(payloadData.tables)
          ? payloadData.tables
          : [];

        const resolvedTableIds = new Set();

        tablePayloads.forEach((tablePayload) => {
          const tableId = String(tablePayload?.tableId || "").trim();
          if (!tableId) return;
          resolvedTableIds.add(tableId);
          const entries = Array.isArray(tablePayload?.entries)
            ? tablePayload.entries
            : Array.isArray(tablePayload?.players)
              ? tablePayload.players
              : [];

          applyTableEntriesPayloadToState({
            state,
            tableId,
            table: tablePayload?.table || null,
            entries,
          });
        });

        const requestedTableIds = Array.isArray(action.meta.arg?.tableIds)
          ? action.meta.arg.tableIds
          : [];

        requestedTableIds.forEach((tableId) => {
          const normalizedTableId = String(tableId || "").trim();
          if (!normalizedTableId || resolvedTableIds.has(normalizedTableId)) return;
          applyTableEntriesPayloadToState({
            state,
            tableId: normalizedTableId,
            table: state.tables[normalizedTableId] || null,
            entries: [],
          });
        });
      })
      .addCase(fetchTournamentEntriesByTableIds.rejected, (state, action) => {
        const requestedTableIds = Array.isArray(action.meta.arg?.tableIds)
          ? action.meta.arg.tableIds
          : [];

        requestedTableIds.forEach((tableId) => {
          const normalizedTableId = String(tableId || "").trim();
          if (!normalizedTableId) return;
          state.status[normalizedTableId] = "failed";
          state.error[normalizedTableId] = action.payload || action.error.message;
        });
      })

      .addCase(movePlayers.pending, (state) => {
        state.status.move = "loading";
      })
      .addCase(movePlayers.fulfilled, (state, action) => {
        const { entryMoves, toTableId } = action.payload;

        entryMoves.forEach((move) => {
          const fromTableId = move.fromTableId;
          const sourceRows = state.data[fromTableId] || [];
          const sourceIndex = findEntryIndex(sourceRows, move);
          if (sourceIndex === -1) return;

          const [entry] = sourceRows.splice(sourceIndex, 1);
          const targetRows = state.data[toTableId] || [];
          const targetIndex = findEntryIndex(targetRows, move);
          if (targetIndex !== -1) {
            targetRows.splice(targetIndex, 1);
          }
          targetRows.push(entry);

          state.data[fromTableId] = sourceRows;
          state.data[toTableId] = targetRows;
        });

        state.status.move = "succeeded";
      })
      .addCase(movePlayers.rejected, (state, action) => {
        state.status.move = "failed";
        state.error.move = action.payload || action.error.message;
      })

      .addCase(moveEntryToNextStage.pending, (state) => {
        state.status.move = "loading";
      })
      .addCase(moveEntryToNextStage.fulfilled, (state) => {
        state.status.move = "succeeded";
      })
      .addCase(moveEntryToNextStage.rejected, (state, action) => {
        state.status.move = "failed";
        state.error.move = action.payload || action.error.message;
      })

      .addCase(moveEntryToFinalStage.pending, (state) => {
        state.status.move = "loading";
      })
      .addCase(moveEntryToFinalStage.fulfilled, (state) => {
        state.status.move = "succeeded";
      })
      .addCase(moveEntryToFinalStage.rejected, (state, action) => {
        state.status.move = "failed";
        state.error.move = action.payload || action.error.message;
      })

      .addCase(deletePlayerFromTable.pending, (state) => {
        state.status.delete = "loading";
        state.error.delete = null;
      })
      .addCase(deletePlayerFromTable.fulfilled, (state, action) => {
        const { tableId, kind, userId, squadId } = action.payload;

        state.data[tableId] = (state.data[tableId] || []).filter((player) => {
          if (kind === "squad") {
            return toStringId(player?.squadId || player?._id) !== toStringId(squadId);
          }
          return toStringId(player?.userId || player?._id) !== toStringId(userId);
        });

        state.selectedPlayers = state.selectedPlayers.filter((id) => {
          if (kind === "squad") return toStringId(id) !== toStringId(squadId);
          return toStringId(id) !== toStringId(userId);
        });

        state.status.delete = "succeeded";
        state.error.delete = null;
      })
      .addCase(deletePlayerFromTable.rejected, (state, action) => {
        state.status.delete = "failed";
        state.error.delete = action.payload || action.error.message;
      })

      .addCase(addPlayerToTournament.pending, (state) => {
        state.addPlayerToTouranemntStatus = "loading";
      })
      .addCase(addPlayerToTournament.fulfilled, (state) => {
        state.addPlayerToTouranemntStatus = "succeeded";
      })
      .addCase(addPlayerToTournament.rejected, (state) => {
        state.addPlayerToTouranemntStatus = "failed";
      })

      .addCase(setTournamentEntryChecked.pending, (state, action) => {
        const rowId = getEntryActionRowId(action.meta.arg);
        if (!rowId) return;
        state.entryCheckStatusByRowId[rowId] = "loading";
      })
      .addCase(setTournamentEntryChecked.fulfilled, (state, action) => {
        const rowId =
          getEntryActionRowId(action.payload) ||
          getEntryActionRowId(action.meta.arg);
        const tableId = toStringId(action.payload?.tableId || action.meta.arg?.tableId);
        const nextChecked = Boolean(action.payload?.isChecked);

        if (tableId) {
          state.data[tableId] = (state.data[tableId] || []).map((entry) => {
            if (getEntryRowId(entry) !== rowId) return entry;
            return {
              ...entry,
              isChecked: nextChecked,
            };
          });
        }

        if (!rowId) return;
        delete state.entryCheckStatusByRowId[rowId];
      })
      .addCase(setTournamentEntryChecked.rejected, (state, action) => {
        const rowId = getEntryActionRowId(action.meta.arg);
        if (!rowId) return;
        delete state.entryCheckStatusByRowId[rowId];
      });
  },
});

export const {
  highlightPlayer,
  resetHighlights,
  setSelectedPlayers,
  resetSelectedPlayers,
  setCutPlayers,
  clearCutPlayers,
  setCutFromTableId,
} = playersSlice.actions;

export default playersSlice.reducer;
